import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatNum } from "../lib/utils";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Cpu, Coins, CheckCircle2, AlertTriangle, Gauge, ShieldCheck, Flag, Layers } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, unit, sub, icon: Icon, testid, tone }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AITransparency() {
  const [d, setD] = useState(null);
  const [log, setLog] = useState([]);

  const load = useCallback(() => {
    api.get("/ai-transparency/dashboard").then((r) => setD(r.data)).catch(() => setD({ empty: true }));
    api.get("/ai-transparency/log").then((r) => setLog(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (id) => { try { await api.post(`/ai-transparency/log/${id}/review`); load(); toast.success("Marked reviewed"); } catch { toast.error("Failed"); } };
  const flag = async (id) => { try { await api.post(`/ai-transparency/log/${id}/flag`); load(); } catch { toast.error("Failed"); } };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading AI transparency…</div>;
  if (d.empty) return <div className="p-8"><div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No AI activity yet.</h2><p className="text-muted-foreground mt-2 max-w-lg mx-auto">{d.governance_note}</p></div></div>;

  const maxModuleCalls = Math.max(...d.by_module.map((m) => m.calls), 1);
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="ai-transparency-page">
      <div>
        <div className="label-eyebrow mb-1">Data Management · AI Use Transparency</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Cpu className="h-8 w-8 text-accent" /> AI Usage & Cost Transparency</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Governance view of where and how AI is used — model usage log, confidence, cost, human review and flags. Strict per-tenant isolation.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="AI calls" value={formatNum(d.total_calls)} unit="" icon={Cpu} testid="ai-stat-calls" />
        <Stat label="Success rate" value={d.success_rate} unit="%" icon={CheckCircle2} testid="ai-stat-success" tone={d.success_rate >= 85 ? "text-[#00E65B]" : "text-[#FFB800]"} sub={`${d.fallback} fallbacks`} />
        <Stat label="Est. cost" value={`S$${d.est_cost_sgd.toFixed(2)}`} unit="" icon={Coins} testid="ai-stat-cost" sub={`${formatNum(d.approx_tokens)} tokens`} />
        <Stat label="Avg confidence" value={d.avg_confidence} unit="" icon={Gauge} testid="ai-stat-confidence" />
        <Stat label="Human reviewed" value={d.human_reviewed} unit="" icon={ShieldCheck} testid="ai-stat-reviewed" />
        <Stat label="Flagged" value={d.flagged} unit="" icon={Flag} testid="ai-stat-flagged" tone={d.flagged ? "text-red-500" : ""} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="ai-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="log" data-testid="ai-tab-log">Model Usage Log</TabsTrigger>
          <TabsTrigger value="governance" data-testid="ai-tab-governance">AI Governance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Usage by module</div>
              <div className="space-y-2">
                {d.by_module.map((m) => (
                  <div key={m.module}><div className="flex justify-between text-xs mb-0.5"><span>{m.module}</span><span className="font-mono">{m.calls} · S${m.cost.toFixed(2)}</span></div>
                    <Progress value={(m.calls / maxModuleCalls) * 100} className="h-1.5" /></div>
                ))}
              </div>
            </div>
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2">Top features</div>
              <div className="space-y-1.5">
                {d.by_feature.map((f) => (
                  <div key={f.feature} className="flex justify-between text-sm"><span className="text-muted-foreground font-mono text-xs">{f.feature}</span><span className="font-mono text-xs">{f.calls}</span></div>
                ))}
              </div>
              <div className="flex justify-between text-xs pt-2 mt-2 border-t border-border"><span className="text-muted-foreground">Models</span><span className="font-mono">{d.models.join(", ")}</span></div>
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Avg latency</span><span className="font-mono">{d.avg_latency_ms} ms</span></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Feature</TableHead><TableHead>Module</TableHead><TableHead>Model</TableHead>
                <TableHead className="text-right">Confidence</TableHead><TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Cost</TableHead><TableHead>Result</TableHead><TableHead>When</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {log.map((r) => (
                  <TableRow key={r.id} data-testid={`ai-log-row-${r.id}`} className={r.flagged ? "bg-red-500/5" : ""}>
                    <TableCell className="text-xs font-mono">{r.feature}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.module}</TableCell>
                    <TableCell className="text-[10px] font-mono text-muted-foreground">{r.model}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.confidence}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(r.approx_tokens)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">S${(r.est_cost_sgd || 0).toFixed(4)}</TableCell>
                    <TableCell>{r.used_fallback ? <Badge variant="warning" className="text-[9px]">Fallback</Badge> : <Badge variant="success" className="text-[9px]">LLM</Badge>}</TableCell>
                    <TableCell className="text-[10px] font-mono text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {r.human_reviewed ? <Badge variant="lime" className="text-[9px]"><CheckCircle2 className="h-3 w-3" /></Badge> : <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => review(r.id)} data-testid={`ai-review-${r.id}`}>Review</Button>}
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => flag(r.id)} data-testid={`ai-flag-${r.id}`}><Flag className={`h-3 w-3 ${r.flagged ? "text-red-500 fill-red-500" : "text-muted-foreground"}`} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="governance" className="mt-4">
          <div className="card-flat p-6 space-y-4">
            <div className="label-eyebrow inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> AI governance policy</div>
            <p className="text-sm leading-relaxed text-muted-foreground" data-testid="ai-governance-note">{d.governance_note}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="border border-border rounded-sm p-3"><div className="font-mono text-lg font-bold text-[#00E65B]">{d.success_rate}%</div><div className="text-[11px] text-muted-foreground">Live-model success</div></div>
              <div className="border border-border rounded-sm p-3"><div className="font-mono text-lg font-bold">{d.human_reviewed}</div><div className="text-[11px] text-muted-foreground">Human-reviewed outputs</div></div>
              <div className="border border-border rounded-sm p-3"><div className="font-mono text-lg font-bold text-[#FFB800]">{d.fallback_rate}%</div><div className="text-[11px] text-muted-foreground">Deterministic fallback rate</div></div>
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
              <ShieldCheck className="h-4 w-4 text-[#00E65B] mt-0.5 shrink-0" />
              <span><strong>Tenant isolation:</strong> all AI calls and logs are scoped to your organisation only. No prompts, data or outputs are shared across tenants.</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
